/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';

// Single home for the retrying-dynamic-import pattern that was copy-pasted across
// LazyAssistantDataProvider, LazyAssistantShell, and the messageTypes importers. Callers pass an
// importer THUNK (`() => import('./Foo')`) — the literal import() specifier stays
// at the call site so Rollup can still code-split it — plus the export name to pull
// off the resolved module.
//
// Options preserve the two behaviours that existed before:
//   - placeholder:       element rendered on the client while the chunk loads
//                        (the messageTypes importers show a Skeleton; the top-level
//                        importers render nothing). Defaults to nothing.
//   - errorBoundaryName: when set, the loaded component is wrapped in an
//                        <ErrorBoundary name=...> (messageTypes behaviour).
//   - rethrowOnError:    when true, a failed import (after retries) is re-thrown
//                        during render so a parent ErrorBoundary catches it
//                        (LazyAssistantDataProvider / LazyAssistantShell behaviour). When false, the
//                        placeholder simply persists, matching the old importers.
//   - retries:           import attempts before giving up (default 3).
const Server = () => (<></>);

export const createLazyComponent = (importer, exportName, {
  label = exportName,
  placeholder = null,
  errorBoundaryName = null,
  rethrowOnError = false,
  retries = 3,
} = {}) => {
  const retryingImport = (remaining = retries) => importer().catch((err) => {
    if (remaining > 0) return retryingImport(remaining - 1);
    throw err;
  });

  const LazyComponent = (props) => {
    const moduleRef = useRef(null);
    const [, forceRender] = useState();
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
      let active = true;
      retryingImport()
        .then((module) => {
          if (!active) return;
          moduleRef.current = module[exportName];
          if (!moduleRef.current) {
            throw new Error(`Error importing ${label}.`);
          }
          forceRender(new Date().getTime());
        })
        .catch((err) => { if (active) setLoadError(err); });
      return () => { active = false; };
    }, []);

    if (rethrowOnError && loadError) throw loadError;
    if (typeof window === 'undefined') return Server();
    if (!moduleRef.current) return placeholder ?? Server();

    const element = (<moduleRef.current {...props} />);
    return errorBoundaryName
      ? <ErrorBoundary name={errorBoundaryName}>{element}</ErrorBoundary>
      : element;
  };

  LazyComponent.displayName = `Lazy(${label})`;
  return LazyComponent;
};
